import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';
import { BaseDatatableStore, IBaseDatatableState } from '../../../../state/base-datatable-store.service';
import { ELoadStatus } from '../../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseCrudResponse,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../../utilities/http-utilities.service';
import { IAiProvider, IEditAiProvider, TAddAiProvider } from './ai-providers.interface';

export interface IAiProviderComponentState extends IBaseDatatableState<IAiProvider> {
  bulkCrudLoading: ELoadStatus;
  bulkOperationFailedData: IBaseCrudResponse[];
  data: IAiProvider[];
  dataLoading: ELoadStatus;
  dataTotal: number;
  singleCrudLoading: ELoadStatus;
}

@Injectable()
export class AiProvidersStore extends BaseDatatableStore<IAiProvider, IAiProviderComponentState> {
  readonly createOne = this.effect((trigger$: Observable<TAddAiProvider>) =>
    trigger$.pipe(
      switchMap((item: TAddAiProvider) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.createItem(item).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly updateOne = this.effect((trigger$: Observable<IEditAiProvider>) =>
    trigger$.pipe(
      switchMap((item: IEditAiProvider) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.updateItem(item).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteOne = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((id: number) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.deleteItem(id).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteBulk = this.effect((trigger$: Observable<number[]>) =>
    trigger$.pipe(
      switchMap((itemIds: number[]) => {
        this.patchState({ bulkCrudLoading: ELoadStatus.loading });

        return this.deleteItems(itemIds).pipe(
          tapResponse(
            (response) =>
              this.patchState({ bulkCrudLoading: ELoadStatus.success, bulkOperationFailedData: response.data }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadItems = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.getItems(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IAiProvider>) =>
              this.patchState({ data: response.data, dataLoading: ELoadStatus.success, dataTotal: response.total }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      bulkCrudLoading: ELoadStatus.initial,
      bulkOperationFailedData: [],
      data: [],
      dataLoading: ELoadStatus.initial,
      dataTotal: 0,
      singleCrudLoading: ELoadStatus.initial,
    });
  }

  private createItem(body: TAddAiProvider): Observable<IBaseResponse<IAiProvider>> {
    return this.http.post<IBaseResponse<IAiProvider>>('ai-providers', body);
  }

  private updateItem(item: IEditAiProvider): Observable<IBaseResponse<IAiProvider>> {
    return this.http.patch<IBaseResponse<IAiProvider>>(`ai-providers/${item.id}`, item.dto);
  }

  private deleteItem(id: number): Observable<IBaseResponse<void>> {
    return this.http.delete<IBaseResponse<void>>(`ai-providers/${id}`);
  }

  private getItems(params: HttpParams): Observable<IGetManyResponse<IAiProvider>> {
    return this.http.get<IGetManyResponse<IAiProvider>>('ai-providers', { params });
  }

  private deleteItems(ids: number[]): Observable<IBaseResponse<IBaseCrudResponse[]>> {
    return this.http.delete<IBaseResponse<IBaseCrudResponse[]>>('ai-providers/bulk/delete', {
      body: { payload: ids },
    });
  }
}
